/**
 * StaffAvailability — Disponibilità prodotti operativa (CRM v7).
 *
 * Riutilizza esattamente le stesse API della Quick Availability di
 * Admin: GET /admin/products (lettura) + POST /admin/products/availability
 * (bulk-set). L'unica differenza è la UI: qui abbiamo un tap-target enorme
 * pensato per il servizio (Disponibile ⇄ Esaurito).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Search, Check, XCircle } from "lucide-react";

export default function StaffAvailability() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState(new Set());
  const [filter, setFilter] = useState("all"); // all | out | in

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/products?limit=1000");
      setRows(Array.isArray(r.data) ? r.data : (r.data?.rows || []));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((p) => {
      if (filter === "out" && p.available !== false) return false;
      if (filter === "in" && p.available === false) return false;
      if (!term) return true;
      const hay = `${p.name} ${p.category_name || ""}`.toLowerCase();
      return hay.includes(term);
    }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [rows, q, filter]);

  const toggle = async (p) => {
    const to = !(p.available === false ? false : true); // true→false; false→true. If undefined → false (mark out).
    const nextVal = p.available === false ? true : false;
    setBusyIds((s) => new Set(s).add(p.id));
    try {
      await api.patch(`/admin/products/${p.id}/availability?available=${nextVal}`);
      setRows((prev) => prev.map((x) => x.id === p.id ? { ...x, available: nextVal } : x));
      toast.success(`${p.name}: ${nextVal ? "🟢 Disponibile" : "🔴 Esaurito"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setBusyIds((s) => { const n = new Set(s); n.delete(p.id); return n; });
    }
  };

  const outCount = rows.filter((p) => p.available === false).length;

  return (
    <div data-testid="staff-availability-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="eyebrow">Servizio</div>
          <h1 className="font-serif text-3xl mt-1">Disponibilità</h1>
          <p className="text-sm text-ink-500 mt-1">
            Tocca un prodotto per marcarlo esaurito o riportarlo disponibile. Effetto immediato sul menu QR.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-ink-500">Esauriti ora</div>
          <div className="font-serif text-3xl tabular-nums" data-testid="avail-out-count">{outCount}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 w-4 h-4 text-ink-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca prodotto…" className="pl-9 h-11 text-base"
                  data-testid="avail-search" />
        </div>
        {[
          { k: "all", label: "Tutti" },
          { k: "out", label: "Solo esauriti" },
          { k: "in",  label: "Solo disponibili" },
        ].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)}
                    data-testid={`avail-filter-${f.k}`}
                    className={`text-sm rounded-full px-3 py-1.5 border
                      ${filter === f.k ? "bg-bronze-600 text-white border-bronze-600"
                                        : "border-sand-300 hover:bg-sand-100"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-ink-400 py-8 text-center">Caricamento…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((p) => {
            const outOfStock = p.available === false;
            const busy = busyIds.has(p.id);
            return (
              <button key={p.id} onClick={() => toggle(p)} disabled={busy}
                        data-testid={`avail-row-${p.id}`}
                        className={`text-left rounded-2xl border-2 p-4 min-h-[80px] transition
                          ${outOfStock ? "bg-rose-50 border-rose-300" : "bg-emerald-50 border-emerald-300"}
                          hover:shadow-md active:scale-[0.99]`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{p.name}</div>
                    {p.category_name && (
                      <div className="text-xs text-ink-500 mt-0.5 truncate">{p.category_name}</div>
                    )}
                  </div>
                  <span className={`text-xs rounded-full px-2 py-0.5 border flex items-center gap-1 whitespace-nowrap
                                     ${outOfStock ? "bg-white border-rose-400 text-rose-800"
                                                    : "bg-white border-emerald-400 text-emerald-800"}`}
                          data-testid={`avail-badge-${p.id}`}>
                    {outOfStock ? (<><XCircle className="w-3 h-3" /> Esaurito</>)
                                : (<><Check className="w-3 h-3" /> Disponibile</>)}
                  </span>
                </div>
                {busy && <div className="text-[10px] text-ink-400 mt-2">…</div>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
